import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import RankMovementLabel from '../leaderboard/RankMovementLabel';
import { homeDarkHeroShell } from './homeDashboardStyles';
import {
  StatTile,
  KpiWithChange,
  WinRateDonut,
  HeroEloDelta,
  heroKpiClass,
  kpiHideBelowMd,
  KpiSkeletonGrid,
  PegBoardSessionTiles,
} from '../session/SessionKpiStrip';
import { SessionLadderToggle } from '../session/SessionLadderToggle';
import ClubLogo from '../clubs/ClubLogo';

function ladderViewFromKey(key) {
  return key === 'mixed' ? 'mixed' : 'levels';
}

function resolveActiveLadder(snapshot, ladderView) {
  const { mixed, levels } = snapshot?.ladders ?? {};
  if (ladderView === 'levels' && levels) return levels;
  return mixed ?? levels ?? snapshot?.primaryLadder ?? null;
}

function ChevronRightIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
    </svg>
  );
}

// ─── Main hero component ──────────────────────────────────────────────────────

/**
 * HomeLastSessionHero — dark welcome panel + last session stats snapshot.
 */
export function HomeLastSessionHero({ currentUser, snapshot, loading }) {
  const navigate = useNavigate();
  const [ladderView, setLadderView] = useState('mixed');

  useEffect(() => {
    if (!snapshot) return;
    setLadderView(ladderViewFromKey(snapshot.defaultLadderKey ?? 'mixed'));
  }, [snapshot?.sessionId, snapshot?.defaultLadderKey]);

  const handleViewSession = useCallback(() => {
    if (!snapshot) return;
    navigate(`/player/${currentUser?.id}/session/${snapshot.sessionId}`, {
      state: { clubId: snapshot.clubId, fromHome: true },
    });
  }, [navigate, currentUser?.id, snapshot]);

  const handleViewClub = useCallback(() => {
    if (!snapshot?.clubId) return;
    navigate(`/clubs/${snapshot.clubId}`, { state: { fromHomepage: true } });
  }, [navigate, snapshot?.clubId]);

  const activeLadder = useMemo(
    () => resolveActiveLadder(snapshot, ladderView),
    [snapshot, ladderView]
  );
  const ladderGamesPlayed = activeLadder?.gamesPlayed ?? 0;
  const winRatePct = activeLadder?.winRatePct ?? null;
  const maxWinStreak = activeLadder?.maxWinStreak ?? 0;
  const canToggleLadder = snapshot?.variant !== 'pegBoard' && Boolean(snapshot?.canToggleLadder);
  const isPegBoard = snapshot?.variant === 'pegBoard';
  const mixedToggleLabel = snapshot?.ladders?.mixed?.toggleLabel ?? 'Mixed';
  const levelsToggleLabel = snapshot?.ladders?.levels?.toggleLabel ?? 'Levels';

  const eloDelta = activeLadder?.eloDelta ?? 0;
  const ladderLabel = activeLadder?.label ?? 'Mixed';
  const rankKpi = activeLadder?.rank != null ? `#${activeLadder.rank}` : '—';
  const eloKpi =
    activeLadder?.eloRating != null ? activeLadder.eloRating.toLocaleString() : '—';

  const lastSessionDateLabel = snapshot?.sessionDate
    ? format(parseISO(snapshot.sessionDate), 'EEE, MMM d')
    : null;

  return (
    <div className={`${homeDarkHeroShell} mb-2 sm:mb-4`}>
      <div className="p-5 sm:p-7">
        {/* Welcome + stats */}
        <div className="flex flex-col min-w-0">
          <div className="mb-4">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white leading-tight">
              Welcome back, {currentUser?.first_name}
              <span className="ml-2 text-[0.85em] font-normal" aria-hidden>👋</span>
            </h1>
            {!snapshot && !loading ? (
              <p className="mt-1.5 text-sm text-white/55">
                Here&apos;s what&apos;s happening in your badminton world
              </p>
            ) : null}
          </div>

          {loading ? (
            <KpiSkeletonGrid hiddenIndexes={[2, 4]} />
          ) : snapshot ? (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <div className="flex items-start sm:items-center gap-2.5 min-w-0 flex-1">
                  {(snapshot.clubLogoImageUrl || snapshot.clubName) ? (
                    <button
                      type="button"
                      onClick={handleViewClub}
                      disabled={!snapshot.clubId}
                      className="shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 disabled:cursor-default"
                      aria-label={snapshot.clubName ? `View ${snapshot.clubName}` : 'View club'}
                    >
                      <ClubLogo
                        logoImageUrl={snapshot.clubLogoImageUrl}
                        clubName={snapshot.clubName || ''}
                        size="sm"
                        className="border-2 border-white/25 shadow-sm"
                      />
                    </button>
                  ) : null}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-white/50 min-w-0">
                  <span className="font-semibold uppercase tracking-wider text-white/60">
                    Your last session
                  </span>
                  <span className="text-white/30" aria-hidden>&middot;</span>
                  <button
                    type="button"
                    onClick={handleViewSession}
                    className="truncate max-w-[min(100%,14rem)] sm:max-w-xs text-white/70 hover:text-white transition-colors underline-offset-2 hover:underline text-left"
                    title={snapshot.sessionName}
                  >
                    {snapshot.sessionName}
                  </button>
                  {lastSessionDateLabel ? (
                    <>
                      <span className="text-white/30" aria-hidden>&middot;</span>
                      <span>{lastSessionDateLabel}</span>
                    </>
                  ) : null}
                  {snapshot.clubName ? (
                    <>
                      <span className="text-white/30" aria-hidden>&middot;</span>
                      <button
                        type="button"
                        onClick={handleViewClub}
                        disabled={!snapshot.clubId}
                        className="truncate max-w-[min(100%,10rem)] sm:max-w-xs text-white/70 hover:text-white transition-colors underline-offset-2 hover:underline text-left disabled:cursor-default disabled:hover:text-white/70 disabled:hover:no-underline"
                        title={snapshot.clubName}
                      >
                        {snapshot.clubName}
                      </button>
                    </>
                  ) : null}
                  </div>
                </div>

                {canToggleLadder ? (
                  <SessionLadderToggle
                    tabs={[
                      { id: 'mixed', label: mixedToggleLabel },
                      { id: 'levels', label: levelsToggleLabel },
                    ]}
                    value={ladderView}
                    onChange={setLadderView}
                  />
                ) : null}
              </div>

              {isPegBoard ? (
                <PegBoardSessionTiles sessionStats={snapshot} />
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 items-stretch">

                <StatTile
                  label={`Rank Change (${ladderLabel})`}
                  kpi={
                    <KpiWithChange
                      value={rankKpi}
                      change={<RankMovementLabel rankDelta={activeLadder?.rankDelta} size="sm" />}
                    />
                  }
                />

                <StatTile
                  label={`Elo Rating (${ladderLabel})`}
                  kpi={
                    <KpiWithChange
                      value={eloKpi}
                      change={<HeroEloDelta delta={eloDelta} />}
                    />
                  }
                />

                <StatTile
                  className={kpiHideBelowMd}
                  label={`Games Played (${ladderLabel})`}
                  kpi={
                    <span className={heroKpiClass}>
                      {ladderGamesPlayed > 0 ? ladderGamesPlayed : '—'}
                    </span>
                  }
                />

                <StatTile
                  label={`Win Rate (${ladderLabel})`}
                  kpi={
                    <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                      <div className="hidden sm:block shrink-0">
                        <WinRateDonut pct={winRatePct} size={32} />
                      </div>
                      <span className={`${heroKpiClass} shrink-0`}>
                        {winRatePct != null ? `${winRatePct}%` : '—'}
                      </span>
                    </div>
                  }
                />

                <StatTile
                  className={kpiHideBelowMd}
                  label={`Win Streak (${ladderLabel})`}
                  kpi={
                    <span className={`${heroKpiClass} flex items-center gap-1.5`}>
                      {maxWinStreak >= 3 && <span className="text-lg sm:text-xl" aria-hidden>🔥</span>}
                      {maxWinStreak}
                    </span>
                  }
                />
              </div>
              )}

              <button
                type="button"
                onClick={handleViewSession}
                className="mt-3 sm:mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white text-[#0e1624] hover:bg-white/92 active:scale-[0.99] px-4 py-3 sm:py-2.5 text-sm font-semibold shadow-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 min-h-[48px] sm:min-h-[44px]"
              >
                View session results
                <ChevronRightIcon />
              </button>
            </>
          ) : (
            <div className="rounded-xl bg-white/[0.05] border border-white/[0.08] px-4 py-5 text-center">
              <p className="text-sm text-white/60 leading-relaxed">
                Play a session to see your stats here
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
